import { useMemo, useState } from 'react';
import { useApp, useFetch } from '../store/app';
import { Fade, FilterSelect } from './common';
import { Icon } from '../components/Icon';
import { Card, Button, Avatar, StatusBadge } from '../components/primitives';
import { st } from '../lib/status';
import { av } from '../lib/avatar';
import type { Invoice } from '../../shared/types';

const COLS = '1.2fr 1.6fr 1.6fr 1fr 1fr 90px';
const money = (s: string) => Number(String(s).replace(/[^0-9.\-]/g, '')) || 0;

export function Invoices() {
  const { openDrawer } = useApp();
  const { data } = useFetch<Invoice[]>('/invoices');
  const allInvoices = data || [];
  const [statusF, setStatusF] = useState('all');
  const invoices = allInvoices.filter((i) => statusF === 'all' || i.status === statusF);

  const statusOpts = useMemo(() => Array.from(new Set(allInvoices.map((i) => i.status))), [allInvoices]);

  const exportCsv = () => {
    const head = ['Invoice', 'From', 'Project', 'Amount', 'Status', 'Date'];
    const rows = invoices.map((i) => [i.num, i.person_name || 'Unassigned', i.project_name, i.amount, i.status, i.date]);
    const esc = (v: string) => `"${String(v).replace(/"/g, '""')}"`;
    const csv = [head, ...rows].map((r) => r.map(esc).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'invoices.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const stats = useMemo(() => {
    const sum = (f: (i: Invoice) => boolean) => allInvoices.filter(f).reduce((s, i) => s + money(i.amount), 0);
    const fmt = (n: number) => `$${n.toLocaleString('en-US')}`;
    const n = (f: (i: Invoice) => boolean) => allInvoices.filter(f).length;
    return [
      { label: 'Awaiting review', value: fmt(sum((i) => i.status === 'pending')), sub: `${n((i) => i.status === 'pending')} invoices`, tone: 'warning' },
      { label: 'Approved (unpaid)', value: fmt(sum((i) => i.status === 'approved')), sub: `${n((i) => i.status === 'approved')} invoices`, tone: 'info' },
      { label: 'Paid this month', value: fmt(sum((i) => i.status === 'paid')), sub: `${n((i) => i.status === 'paid')} invoices`, tone: 'success' },
    ];
  }, [allInvoices]);

  return (
    <Fade>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14, marginBottom: 16 }}>
        {stats.map((s) => (
          <Card key={s.label} style={{ padding: 16, borderRadius: 11 }}>
            <div style={{ fontSize: 12.5, color: 'var(--text-2)', fontWeight: 500 }}>{s.label}</div>
            <div className="cc-mono" style={{ fontSize: 24, fontWeight: 700, letterSpacing: '-.02em', margin: '4px 0', color: `var(--${s.tone})` }}>{s.value}</div>
            <div style={{ fontSize: 12, color: 'var(--text-3)' }}>{s.sub}</div>
          </Card>
        ))}
      </div>
      <Card style={{ overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '13px 16px', borderBottom: '1px solid var(--border)' }}>
          <FilterSelect icon="filter_list" value={statusF} onChange={setStatusF} options={[{ label: 'All statuses', value: 'all' }, ...statusOpts.map((s) => ({ label: st(s).label, value: s }))]} />
          <div style={{ flex: 1 }} />
          <Button variant="secondary" icon="download" onClick={exportCsv}>Export</Button>
          <Button icon="add" onClick={() => openDrawer({ type: 'invoice-new', mode: 'create' })}>Submit invoice</Button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: COLS, gap: 14, padding: '10px 18px', borderBottom: '1px solid var(--border)', fontSize: 11, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '.04em' }}>
          <div>Invoice</div><div>From</div><div>Project</div><div>Amount</div><div>Status</div><div />
        </div>
        {invoices.map((inv) => {
          const a = av(inv.person_name, inv.person_kind);
          return (
            <div key={inv.id} onClick={() => openDrawer({ type: 'invoice-view', mode: 'view', id: inv.id })} className="cc-hover-s2 cc-clickable" style={{ display: 'grid', gridTemplateColumns: COLS, gap: 14, padding: '12px 18px', borderBottom: '1px solid var(--border)', alignItems: 'center' }}>
              <div>
                <div className="cc-mono" style={{ fontWeight: 600, fontSize: 13 }}>{inv.num}</div>
                <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{inv.date}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 9, minWidth: 0 }}>
                <Avatar initials={a.in} bg={a.bg} fg={a.fg} size={28} />
                <span style={{ fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{inv.person_name || 'Unassigned'}</span>
              </div>
              <div style={{ fontSize: 13, color: 'var(--text-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{inv.project_name}</div>
              <div className="cc-mono" style={{ fontSize: 13, fontWeight: 600 }}>{inv.amount}</div>
              <div><StatusBadge badge={st(inv.status)} /></div>
              <div style={{ textAlign: 'right' }}>
                <button onClick={(e) => { e.stopPropagation(); openDrawer({ type: 'invoice-view', mode: 'view', id: inv.id }); }} style={{ appearance: 'none', border: 0, background: 'transparent', color: 'var(--accent)', fontFamily: 'inherit', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}>
                  {inv.status === 'pending' ? 'Review' : 'View'}
                </button>
              </div>
            </div>
          );
        })}
      </Card>
    </Fade>
  );
}
